// TODO(tyan): remove this
/* eslint class-methods-use-this: 0 */
/* eslint-disable no-console */

import axios from 'axios';
import {
  BaseMetricsRequest,
  GetContentAccuracyDetailMetricsForProductResponse,
  GetContentAccuracySummaryResponse,
  GetContentMetricsForProductRequest,
  GetContentResponse,
  GetContentScoreExtendedSummaryByWeekResponse,
  GetContentScoreSummaryResponse,
  GetContentUtilizationDetailMetricsForProductResponse,
  GetContentUtilizationSummaryResponse,
  GetKeywordPerformanceScoreSummaryResponse,
  GetProductCountByToneTypeResponse,
  GetProductCountByVoiceTypeResponse,
  GetScoreForContentMetricByCategoryTypeRequest,
  GetScoreForContentMetricByCategoryTypeResponse,
  GetScoreForContentMetricByProductResponse,
  GetScoreForContentMetricByWeekResponse,
  GetScoreForContentMetricRequest,
  GetVoiceAndToneAnalysisByProductResponse,
  GetVoiceAndToneCurrentAndTargetVectorsResponse,
  GetWeightedKeywordsAndPhrasesForProductRequest,
  GetWeightedKeywordsAndPhrasesForProductResponse,
  VoiceAndToneMetricsRequest
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';

/**
 * Interface defining the contract for interacting with the content server.
 * Provides methods for retrieving various content metrics and analytics.
 */
interface ContentServerProxy {
  getScoreForContentMetricByWeek: (
    req: GetScoreForContentMetricRequest
  ) => Promise<GetScoreForContentMetricByWeekResponse>;

  getScoreForContentMetricByCategoryType: (
    req: GetScoreForContentMetricByCategoryTypeRequest
  ) => Promise<GetScoreForContentMetricByCategoryTypeResponse | any>; // FIXME: response type when defined

  getScoreForContentMetricByProduct: (
    req: GetScoreForContentMetricRequest
  ) => Promise<GetScoreForContentMetricByProductResponse>; // TODO: update response when defined

  getContentScoreSummary: (req: BaseMetricsRequest) => Promise<GetContentScoreSummaryResponse>;

  getContentUtilizationSummary: (req: BaseMetricsRequest) => Promise<GetContentUtilizationSummaryResponse>;

  getContentAccuracySummary: (req: BaseMetricsRequest) => Promise<GetContentAccuracySummaryResponse>;

  getKeywordPerformanceScoreSummary: (req: BaseMetricsRequest) => Promise<GetKeywordPerformanceScoreSummaryResponse>;

  getContentUtilizationDetailMetricsForProduct: (
    req: GetContentMetricsForProductRequest
  ) => Promise<GetContentUtilizationDetailMetricsForProductResponse>;

  getContentAccuracyDetailMetricsForProduct: (
    req: GetContentMetricsForProductRequest
  ) => Promise<GetContentAccuracyDetailMetricsForProductResponse>;

  getProductCountByVoiceAndToneType: (req: VoiceAndToneMetricsRequest) => Promise<any>; // TODO: update response when defined

  getVoiceAndToneAnalysisByProduct: (
    req: VoiceAndToneMetricsRequest
  ) => Promise<GetVoiceAndToneAnalysisByProductResponse>;

  getContentScoreExtendedSummaryByWeek: (
    req: BaseMetricsRequest
  ) => Promise<GetContentScoreExtendedSummaryByWeekResponse>;

  getWeightedKeywordsAndPhrasesForProduct: (
    req: GetWeightedKeywordsAndPhrasesForProductRequest
  ) => Promise<GetWeightedKeywordsAndPhrasesForProductResponse>;

  getVoiceAndToneCurrentAndTargetVectors: (
    req: GetContentMetricsForProductRequest
  ) => Promise<GetVoiceAndToneCurrentAndTargetVectorsResponse>;

  getCurrentContent: (req: GetContentMetricsForProductRequest) => Promise<GetContentResponse>;

  getApprovedContent: (req: GetContentMetricsForProductRequest) => Promise<GetContentResponse>;

  getProductCountByVoiceType: (req: BaseMetricsRequest) => Promise<GetProductCountByVoiceTypeResponse>;

  getProductCountByToneType: (req: BaseMetricsRequest) => Promise<GetProductCountByToneTypeResponse>;
}

// This class is used to make requests to the content server and handle responses
export class ContentServerProxyImpl implements ContentServerProxy {
  private readonly baseUrl: string;

  private readonly axiosInstance: typeof axios;

  constructor(baseUrl: string = '/api/beaconcontent', axiosInstance = axios) {
    this.baseUrl = baseUrl;
    this.axiosInstance = axiosInstance;
  }

  private removeEmptyValues(obj: Record<string, any>): Record<string, any> {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      // Skip empty arrays, empty strings, and undefined values while keeping other truthy values
      if (value !== undefined && value !== '' && [value].flat().length) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private async makeRequest<T>(endpoint: string, req: any): Promise<T> {
    try {
      const request = this.removeEmptyValues(req);
      console.debug(`Request to ${endpoint}:`, request);

      const response = await this.axiosInstance.post(`${this.baseUrl}/${endpoint}`, request);
      if (response.status !== 200) {
        throw new Error(`Request to ${endpoint} failed with status code ${response.status}`);
      }
      console.debug(`Response from ${endpoint}:`, response.data);

      return response.data;
    } catch (error) {
      return this.handleError(error, `Request to ${endpoint} failed`);
    }
  }

  private handleError(error: unknown, message: string): never {
    console.error(message, error);
    throw error;
  }

  async getScoreForContentMetricByWeek(
    req: GetScoreForContentMetricRequest
  ): Promise<GetScoreForContentMetricByWeekResponse> {
    return this.makeRequest<GetScoreForContentMetricByWeekResponse>('GetScoreForContentMetricByWeek', req);
  }

  async getScoreForContentMetricByCategoryType(
    req: GetScoreForContentMetricByCategoryTypeRequest
  ): Promise<GetScoreForContentMetricByCategoryTypeResponse> {
    return this.makeRequest<GetScoreForContentMetricByCategoryTypeResponse>(
      'GetScoreForContentMetricByCategoryType',
      req
    );
  }

  async getScoreForContentMetricByProduct(
    req: GetScoreForContentMetricRequest
  ): Promise<GetScoreForContentMetricByProductResponse> {
    return this.makeRequest<GetScoreForContentMetricByProductResponse>('GetScoreForContentMetricByProduct', req);
  }

  async getContentScoreSummary(req: BaseMetricsRequest): Promise<GetContentScoreSummaryResponse> {
    return this.makeRequest<GetContentScoreSummaryResponse>('GetContentScoreSummary', req);
  }

  async getContentUtilizationSummary(req: BaseMetricsRequest): Promise<GetContentUtilizationSummaryResponse> {
    return this.makeRequest<GetContentUtilizationSummaryResponse>('GetContentUtilizationSummary', req);
  }

  async getContentAccuracySummary(req: BaseMetricsRequest): Promise<GetContentAccuracySummaryResponse> {
    return this.makeRequest<GetContentAccuracySummaryResponse>('GetContentAccuracySummary', req);
  }

  async getKeywordPerformanceScoreSummary(req: BaseMetricsRequest): Promise<GetKeywordPerformanceScoreSummaryResponse> {
    return this.makeRequest<GetKeywordPerformanceScoreSummaryResponse>('GetKeywordPerformanceScoreSummary', req);
  }

  async getProductCountByVoiceAndToneType(): Promise<any> {
    console.debug('Request to getProductCountByVoiceAndToneType');
    const result = { data: [] };
    console.debug('Response from getProductCountByVoiceAndToneType:', result);
    return result;
  }

  async getVoiceAndToneAnalysisByProduct(): Promise<GetVoiceAndToneAnalysisByProductResponse> {
    console.debug('Request to getVoiceAndToneAnalysisByProduct');
    const result = { records: [] };
    console.debug('Response from getVoiceAndToneAnalysisByProduct:', result);
    return result;
  }

  async getContentUtilizationDetailMetricsForProduct(
    req: GetContentMetricsForProductRequest
  ): Promise<GetContentUtilizationDetailMetricsForProductResponse> {
    return this.makeRequest<GetContentUtilizationDetailMetricsForProductResponse>(
      'GetContentUtilizationDetailMetricsForProduct',
      req
    );
  }

  async getContentAccuracyDetailMetricsForProduct(
    req: GetContentMetricsForProductRequest
  ): Promise<GetContentAccuracyDetailMetricsForProductResponse> {
    return this.makeRequest<GetContentAccuracyDetailMetricsForProductResponse>(
      'GetContentAccuracyDetailMetricsForProduct',
      req
    );
  }

  async getContentScoreExtendedSummaryByWeek(
    req: BaseMetricsRequest
  ): Promise<GetContentScoreExtendedSummaryByWeekResponse> {
    return this.makeRequest<GetContentScoreExtendedSummaryByWeekResponse>('GetContentScoreExtendedSummaryByWeek', req);
  }

  async getWeightedKeywordsAndPhrasesForProduct(
    req: GetWeightedKeywordsAndPhrasesForProductRequest
  ): Promise<GetWeightedKeywordsAndPhrasesForProductResponse> {
    return this.makeRequest<GetWeightedKeywordsAndPhrasesForProductResponse>(
      'GetWeightedKeywordsAndPhrasesForProduct',
      req
    );
  }

  async getVoiceAndToneCurrentAndTargetVectors(
    req: GetContentMetricsForProductRequest
  ): Promise<GetVoiceAndToneCurrentAndTargetVectorsResponse> {
    return this.makeRequest<GetVoiceAndToneCurrentAndTargetVectorsResponse>(
      'GetVoiceAndToneCurrentAndTargetVectors',
      req
    );
  }

  async getCurrentContent(req: GetContentMetricsForProductRequest): Promise<GetContentResponse> {
    return this.makeRequest<GetContentResponse>('GetCurrentContent', req);
  }

  async getApprovedContent(req: GetContentMetricsForProductRequest): Promise<GetContentResponse> {
    return this.makeRequest<GetContentResponse>('GetApprovedContent', req);
  }

  async getProductCountByVoiceType(req: BaseMetricsRequest): Promise<GetProductCountByVoiceTypeResponse> {
    return this.makeRequest<GetProductCountByVoiceTypeResponse>('GetProductCountByVoiceType', req);
  }

  async getProductCountByToneType(req: BaseMetricsRequest): Promise<GetProductCountByToneTypeResponse> {
    return this.makeRequest<GetProductCountByToneTypeResponse>('GetProductCountByToneType', req);
  }
}
